/**
 * DroneDB Globe 3D - Interactive Earth visualization with dataset pins
 * Uses Globe.gl library for WebGL rendering
 */

(function() {
    'use strict';

    // Generated by `gulp globeData` from src/globe/datasets.json and inlined by functions.php
    const DATASETS = (window.DRONEDB_GLOBE_DATASETS || []).map(d => Object.assign({}, d, {
        randomHeight: 0.06 + Math.random() * 0.04
    }));

    // Type icons mapping
    const TYPE_ICONS = {
        'Orthophoto': 'fa-map',
        'Point Cloud': 'fa-cubes',
        'Panorama': 'fa-panorama',
        'Flight Path': 'fa-route',
        '3D Model': 'fa-cube',
        'Multispectral': 'fa-layer-group',
        'Thermal': 'fa-temperature-high'
    };

    // Type colors
    const TYPE_COLORS = {
        'Orthophoto': '#00C4CC',
        'Point Cloud': '#6C5CE7',
        'Panorama': '#FF6B6B',
        'Flight Path': '#4ECDC4',
        '3D Model': '#45B7D1',
        'Multispectral': '#9BE15D',
        'Thermal': '#FF8A3D'
    };

    // Pin radius and minimum distance between pin centers, in degrees of arc.
    // Pins closer than PIN_SPACING are fanned out around their centroid so that
    // cylinders never share space (coplanar walls z-fight and flicker).
    const PIN_RADIUS = 0.55;
    const PIN_SPACING = 1.4;

    // Highlighted pin (hovered, or shown by the tour): 1.4x still clears
    // neighbours at PIN_SPACING. The tour looks at its pin from straight above,
    // where a wider pin barely shows, so a white ring pulses around it too;
    // the other pins have no ring.
    const HIGHLIGHT_RADIUS = PIN_RADIUS * 1.4;
    const HIGHLIGHT_RING_RADIUS = PIN_RADIUS * 6;

    // Groups datasets whose pins would overlap and assigns each one a display
    // position (pinLat/pinLng) on a circle around the group centroid.
    // The real lat/lng are left untouched.
    function layoutPins(datasets) {
        const isNear = (a, b) =>
            Math.abs(a.lat - b.lat) < PIN_SPACING &&
            Math.abs(a.lng - b.lng) * Math.cos(a.lat * Math.PI / 180) < PIN_SPACING;

        // Single-link grouping: a dataset near several groups merges them
        let groups = [];
        datasets.forEach(d => {
            const near = groups.filter(g => g.some(m => isNear(m, d)));
            groups = groups.filter(g => !near.includes(g));
            groups.push([d].concat(...near));
        });

        groups.forEach(group => {
            const lat = group.reduce((s, d) => s + d.lat, 0) / group.length;
            const lng = group.reduce((s, d) => s + d.lng, 0) / group.length;
            const ringRadius = group.length > 1 ? PIN_SPACING / (2 * Math.sin(Math.PI / group.length)) : 0;
            const lngScale = 1 / Math.cos(lat * Math.PI / 180);

            group.forEach((d, i) => {
                const angle = 2 * Math.PI * i / group.length;
                d.pinLat = lat + ringRadius * Math.sin(angle);
                d.pinLng = lng + ringRadius * Math.cos(angle) * lngScale;
            });
        });

        return datasets;
    }

    // Tour: flies to each pin and shows its card, until the user interacts
    const TOUR_START_DELAY = 3000;
    const TOUR_FLY_MS = 2000;
    const TOUR_DWELL_MS = 4500;
    const TOUR_GAP_MS = 600;
    const TOUR_ALTITUDE = 1.4;

    class DroneDBGlobe {
        constructor(containerId) {
            this.container = document.getElementById(containerId);
            if (!this.container) {
                console.warn('Globe container not found:', containerId);
                return;
            }

            this.globe = null;
            this.isInitialized = false;
            this.hoveredPoint = null;
            this.highlighted = null;    // dataset whose pin is highlighted
            this.tooltip = null;
            this.tour = {
                stopped: false,     // the user interacted: never resumes
                hovered: false,     // pointer over the globe: paused
                inView: true,       // globe scrolled out of view: paused
                index: 0,
                startAt: 0,
                timer: null,
                current: null,      // dataset whose card is shown
                frame: null
            };

            this.init();
        }

        init() {
            // Create tooltip element
            this.createTooltip();

            // Wait for Globe.gl to load
            if (typeof Globe === 'undefined') {
                console.warn('Globe.gl not loaded yet, waiting...');
                setTimeout(() => this.init(), 100);
                return;
            }

            // Load countries GeoJSON then create globe
            this.loadCountriesData();
        }

        async loadCountriesData() {
            try {
                const response = await fetch('https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson');
                this.countriesData = await response.json();
            } catch (e) {
                console.warn('Could not load countries data:', e);
                this.countriesData = { features: [] };
            }

            this.createGlobe();
            this.addEventListeners();
            this.isInitialized = true;
            this.startTour();
        }

        createTooltip() {
            this.tooltip = document.createElement('div');
            this.tooltip.className = 'globe-tooltip';
            this.tooltip.innerHTML = `
                <div class="tooltip-content">
                    <div class="tooltip-type"></div>
                    <div class="tooltip-thumbnail"></div>
                    <div class="tooltip-name"></div>
                    <div class="tooltip-description"></div>
                    <div class="tooltip-cta">Click to explore <i class="fas fa-arrow-up"></i></div>
                </div>
            `;
            document.body.appendChild(this.tooltip);
        }

        createGlobe() {
            const width = this.container.offsetWidth;
            const height = this.container.offsetHeight || 500;

            this.globe = Globe()
                // Transparent globe - no texture, just outlines
                .showGlobe(true)
                .showAtmosphere(false)
                .globeImageUrl(null)
                .width(width)
                .height(height)
                .backgroundColor('rgba(0,0,0,0)')
                // Countries polygons with outline effect - no sides (no lines to center)
                .polygonsData(this.countriesData?.features || [])
                .polygonCapColor(() => 'rgba(255, 255, 255, 0.03)')
                // Default 5° leaves large flat cap triangles that dip below the
                // sphere surface and show up as dark patches
                .polygonCapCurvatureResolution(1)
                .polygonSideColor(() => 'rgba(0, 0, 0, 0)')
                .polygonStrokeColor(() => 'rgba(255, 255, 255, 0.4)')
                .polygonAltitude(0.001)
                // Points configuration - pins with random heights
                .pointsData(layoutPins(DATASETS))
                .pointLat('pinLat')
                .pointLng('pinLng')
                .pointColor(d => TYPE_COLORS[d.type] || '#00C4CC')
                .pointAltitude(d => d.randomHeight)
                .pointRadius(PIN_RADIUS)
                .pointsMerge(false)
                // Pulsing ring around the highlighted pin only (see highlightPin)
                .ringLat(r => r.d.pinLat)
                .ringLng(r => r.d.pinLng)
                .ringColor(() => t => `rgba(255, 255, 255, ${1 - t})`)
                .ringMaxRadius(HIGHLIGHT_RING_RADIUS)
                .ringPropagationSpeed(4)
                .ringRepeatPeriod(900)
                // Interactivity
                .onPointClick(this.handlePointClick.bind(this))
                .onPointHover(this.handlePointHover.bind(this))
                // Labels
                /*.labelsData(DATASETS)
                .labelLat('lat')
                .labelLng('lng')
                .labelText('name')
                .labelSize(1.8)
                .labelDotRadius(1.0)
                .labelColor(() => 'rgba(255, 255, 255, 0.95)')
                .labelResolution(2)
                .labelAltitude(0.09)*/
                (this.container);

            // Style the globe material for transparent effect
            this.styleGlobeMaterial();
            this.addOccluder();

            // Auto-rotate - SLOWER
            this.globe.controls().autoRotate = true;
            this.globe.controls().autoRotateSpeed = 0.3;
            this.globe.controls().enableZoom = false;

            // Set initial position - lower altitude = bigger globe
            this.globe.pointOfView({ lat: 30, lng: 10, altitude: 1.4 });
        }

        // The globe is almost fully transparent, so without an occluder the far
        // hemisphere (country outlines and pins) shows through and overlaps the
        // near one. This sphere writes depth only, before anything else is drawn.
        addOccluder() {
            const radius = this.globe.getGlobeRadius() * 0.999;
            const occluder = new THREE.Mesh(
                new THREE.SphereGeometry(radius, 64, 64),
                new THREE.MeshBasicMaterial({ colorWrite: false })
            );
            occluder.renderOrder = -1;
            this.globe.scene().add(occluder);
        }

        // Highlights the pin of a dataset, or clears the highlight when d is null.
        // Each layer binds its three.js object to the datum (__threeObj), so the
        // ring wraps the dataset: sharing it with the points layer makes pin
        // updates hit the ring instead.
        highlightPin(d) {
            if (!this.globe || this.highlighted === d) return;

            this.highlighted = d;
            this.globe
                .pointRadius(p => p === d ? HIGHLIGHT_RADIUS : PIN_RADIUS)
                .ringsData(d ? [{ d }] : []);
        }

        styleGlobeMaterial() {
            // Wait for the globe to be ready, then style it
            setTimeout(() => {
                if (this.globe) {
                    const globeMaterial = this.globe.globeMaterial();
                    if (globeMaterial) {
                        // Make globe very transparent - we want to see mainly the country outlines
                        globeMaterial.opacity = 0.08;
                        globeMaterial.transparent = true;
                        globeMaterial.color.setHex(0x0041E8);
                    }
                }
            }, 100);
        }

        handlePointClick(point) {
            if (point && point.url) {
                window.open(point.url, '_blank');
            }
        }

        handlePointHover(point) {
            // While the tour moves the camera, pins slide under a cursor that has not
            // moved over the globe: that is not the user hovering, ignore it
            if (!this.tour.stopped && !this.tour.hovered) return;

            this.hoveredPoint = point;

            // Hovering a pin is an interaction: the tour stops for good
            if (point) this.stopTour();

            this.highlightPin(point);

            // Pause rotation on hover
            if (this.globe && this.globe.controls()) {
                this.globe.controls().autoRotate = !point;
            }

            if (point) {
                this.showTooltip(point);
                this.container.style.cursor = 'pointer';
            } else {
                this.hideTooltip();
                this.container.style.cursor = 'grab';
            }
        }

        // followCursor: false when the tour anchors the card to the pin instead
        showTooltip(point, followCursor = true) {
            const typeEl = this.tooltip.querySelector('.tooltip-type');
            const nameEl = this.tooltip.querySelector('.tooltip-name');
            const descEl = this.tooltip.querySelector('.tooltip-description');
            const thumbEl = this.tooltip.querySelector('.tooltip-thumbnail');

            // Set thumbnail if available (names come from Hub data: no innerHTML)
            thumbEl.replaceChildren();
            if (point.thumbnail) {
                const img = document.createElement('img');
                img.src = point.thumbnail;
                img.alt = `${point.name} thumbnail`;
                thumbEl.appendChild(img);
                thumbEl.style.display = 'block';
            } else {
                thumbEl.style.display = 'none';
            }

            const icon = document.createElement('i');
            icon.className = `fas ${TYPE_ICONS[point.type] || 'fa-database'}`;
            typeEl.replaceChildren(icon, ` ${point.type}`);
            typeEl.style.color = TYPE_COLORS[point.type] || '#00C4CC';
            nameEl.textContent = point.name;
            descEl.textContent = point.description;

            this.tooltip.classList.add('visible');

            // Position tooltip near cursor
            if (followCursor) document.addEventListener('mousemove', this.positionTooltip);
        }

        hideTooltip() {
            this.tooltip.classList.remove('visible');
            document.removeEventListener('mousemove', this.positionTooltip);
        }

        positionTooltip = (e) => {
            this.placeTooltip(e.clientX + 15, e.clientY + 15);
        }

        placeTooltip(x, y) {
            // Prevent tooltip from going off screen
            const rect = this.tooltip.getBoundingClientRect();
            const maxX = window.innerWidth - rect.width - 20;
            const maxY = window.innerHeight - rect.height - 20;

            this.tooltip.style.left = Math.max(0, Math.min(x, maxX)) + 'px';
            this.tooltip.style.top = Math.max(0, Math.min(y, maxY)) + 'px';
        }

        // ---- Tour ----

        startTour() {
            const reducedMotion = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (reducedMotion || DATASETS.length === 0) {
                this.tour.stopped = true;
                return;
            }

            if ('IntersectionObserver' in window) {
                new IntersectionObserver(entries => {
                    this.tour.inView = entries[0].isIntersecting;
                    this.tour.inView ? this.resumeTour() : this.pauseTour();
                }).observe(this.container);
            }

            document.addEventListener('visibilitychange', () => {
                document.hidden ? this.pauseTour() : this.resumeTour();
            });

            // Every (re)schedule honours the start delay, including the observer's
            // initial callback, which fires right away
            this.tour.startAt = performance.now() + TOUR_START_DELAY;
            this.resumeTour();
        }

        tourCanRun() {
            const t = this.tour;
            return !t.stopped && !t.hovered && t.inView && !document.hidden;
        }

        tourStep() {
            clearTimeout(this.tour.timer);
            if (!this.tourCanRun()) return;

            const d = DATASETS[this.tour.index % DATASETS.length];
            this.tour.index++;

            this.globe.controls().autoRotate = false;
            this.globe.pointOfView({ lat: d.pinLat, lng: d.pinLng, altitude: TOUR_ALTITUDE }, TOUR_FLY_MS);
            this.tour.timer = setTimeout(() => this.showTourStop(d), TOUR_FLY_MS);
        }

        showTourStop(d) {
            if (!this.tourCanRun()) return;

            this.tour.current = d;
            this.highlightPin(d);
            this.showTooltip(d, false);
            this.anchorTourTooltip();

            this.tour.timer = setTimeout(() => {
                this.hideTourStop();
                this.tour.timer = setTimeout(() => this.tourStep(), TOUR_GAP_MS);
            }, TOUR_DWELL_MS);
        }

        // Keeps the card next to the top of the pin, also while the page scrolls.
        // The card goes right of the pin, else left; when neither side fits (phones)
        // a compact card without thumbnail is docked at the bottom of the globe,
        // clear of the pin, which the tour keeps at the center.
        anchorTourTooltip() {
            const d = this.tour.current;
            if (!d) return;

            const p = this.globe.getScreenCoords(d.pinLat, d.pinLng, d.randomHeight);
            const rect = this.container.getBoundingClientRect();
            const px = rect.left + p.x;
            const py = rect.top + p.y;
            const gap = 20;
            const thumbEl = this.tooltip.querySelector('.tooltip-thumbnail');
            const width = this.tooltip.offsetWidth;

            if (px + gap + width <= window.innerWidth - gap) {
                this.placeTooltip(px + gap, py - 40);
            } else if (px - gap - width >= gap) {
                this.placeTooltip(px - gap - width, py - 40);
            } else {
                thumbEl.style.display = 'none';
                this.placeTooltip(rect.left + (rect.width - width) / 2,
                    rect.bottom - this.tooltip.offsetHeight - 8);
            }

            this.tour.frame = requestAnimationFrame(() => this.anchorTourTooltip());
        }

        hideTourStop() {
            if (!this.tour.current) return;

            this.tour.current = null;
            cancelAnimationFrame(this.tour.frame);
            this.hideTooltip();
            this.highlightPin(null);
        }

        pauseTour() {
            clearTimeout(this.tour.timer);
            this.hideTourStop();
        }

        resumeTour() {
            clearTimeout(this.tour.timer);
            if (!this.tourCanRun()) return;

            const delay = Math.max(TOUR_GAP_MS, this.tour.startAt - performance.now());
            this.tour.timer = setTimeout(() => this.tourStep(), delay);
        }

        stopTour() {
            if (this.tour.stopped) return;

            this.tour.stopped = true;
            this.pauseTour();
        }

        addEventListeners() {
            // Handle window resize
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    if (this.globe && this.container) {
                        this.globe.width(this.container.offsetWidth);
                        this.globe.height(this.container.offsetHeight || 500);
                    }
                }, 250);
            });

            // Stop rotation when mouse enters container
            this.container.addEventListener('mouseenter', () => {
                this.tour.hovered = true;
                this.pauseTour();
                if (this.globe && this.globe.controls()) {
                    this.globe.controls().autoRotate = false;
                }
            });

            // A cursor already resting on the globe at load gets no mouseenter
            this.container.addEventListener('pointermove', () => {
                if (this.tour.hovered) return;
                this.tour.hovered = true;
                this.pauseTour();
            }, { passive: true });

            // Resume rotation when mouse leaves container
            this.container.addEventListener('mouseleave', () => {
                if (this.globe && this.globe.controls()) {
                    this.globe.controls().autoRotate = true;
                    this.hideTooltip();
                }
                this.tour.hovered = false;
                this.resumeTour();
            });

            // Any real interaction ends the tour for good
            ['mousedown', 'touchstart', 'wheel'].forEach(type =>
                this.container.addEventListener(type, () => this.stopTour(), { passive: true }));

            // Pause rotation when mouse is down (dragging)
            this.container.addEventListener('mousedown', () => {
                if (this.globe && this.globe.controls()) {
                    this.globe.controls().autoRotate = false;
                }
            });

            // Mobile touch handling: disable globe interaction with one finger to allow scroll
            this.container.addEventListener('touchstart', (e) => {
                if (this.globe && this.globe.controls()) {
                    if (e.touches.length === 1) {
                        // Single finger: disable controls completely to allow page scroll
                        this.globe.controls().enabled = false;
                        this.globe.controls().autoRotate = false;
                    } else {
                        // Multiple fingers: enable controls for rotation
                        this.globe.controls().enabled = true;
                        this.globe.controls().autoRotate = false;
                    }
                }
            }, { passive: true });

            this.container.addEventListener('touchmove', (e) => {
                if (this.globe && this.globe.controls()) {
                    if (e.touches.length === 1) {
                        // Single finger: keep controls disabled
                        this.globe.controls().enabled = false;
                    } else {
                        // Multiple fingers: keep controls enabled
                        this.globe.controls().enabled = true;
                    }
                }
            }, { passive: true });

            this.container.addEventListener('touchend', (e) => {
                if (this.globe && this.globe.controls()) {
                    // Re-enable controls and auto-rotation when all touches end
                    if (e.touches.length === 0) {
                        this.globe.controls().enabled = true;
                        this.globe.controls().autoRotate = true;
                    } else if (e.touches.length === 1) {
                        // If one finger remains, disable controls again
                        this.globe.controls().enabled = false;
                    }
                }
            }, { passive: true });
        }

        destroy() {
            if (this.tooltip && this.tooltip.parentNode) {
                this.tooltip.parentNode.removeChild(this.tooltip);
            }
            if (this.globe) {
                this.globe._destructor && this.globe._destructor();
            }
        }
    }

    // Initialize globe when DOM is ready
    function initGlobe() {
        const globeContainer = document.getElementById('globe-container');
        if (globeContainer) {
            window.dronedbGlobe = new DroneDBGlobe('globe-container');
        }
    }

    // Check if Globe.gl is loaded, otherwise wait
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initGlobe);
    } else {
        initGlobe();
    }

    // Export for external use
    window.DroneDBGlobe = DroneDBGlobe;

})();
